import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Check, Clock, ArrowRight, Sun, Sparkles, Flower2, MessageCircle, RefreshCw, XCircle, Mail } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.15 } })
};

const SESSION_ICONS = {
  'happiness-yoga': Sun,
  'happiness-affirmations': Sparkles,
  'nirvana-meditation': Flower2,
};

const SESSION_GRADIENTS = {
  'happiness-yoga': 'from-amber-500 to-orange-500',
  'happiness-affirmations': 'from-indigo-500 to-purple-500',
  'nirvana-meditation': 'from-teal-500 to-emerald-500',
};

const SESSION_BG = {
  'happiness-yoga': 'bg-amber-50 border-amber-100',
  'happiness-affirmations': 'bg-indigo-50 border-indigo-100',
  'nirvana-meditation': 'bg-teal-50 border-teal-100',
};

const SESSION_ACCENT = {
  'happiness-yoga': 'text-amber-600',
  'happiness-affirmations': 'text-indigo-600',
  'nirvana-meditation': 'text-teal-600',
};

const WHATSAPP_NUMBER = '917510792454';

export default function ServicesPage() {
  const [sessions, setSessions] = useState([]);
  const [joinOpen, setJoinOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [successSessionTitle, setSuccessSessionTitle] = useState('');
  const [searchParams] = useSearchParams();

  // Manage subscriptions state
  const [manageOpen, setManageOpen] = useState(false);
  const [lookupEmail, setLookupEmail] = useState('');
  const [activeSubs, setActiveSubs] = useState([]);
  const [lookingUp, setLookingUp] = useState(false);
  const [cancelling, setCancelling] = useState(null);

  useEffect(() => {
    axios.get(`${API}/services`).then(res => setSessions(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session');
    if (payment === 'success' && sessionId) {
      const matched = sessions.find(s => s.id === sessionId);
      setSuccessSessionTitle(matched?.title || 'your session');
      setPaymentSuccess(true);
      setJoinOpen(true);
    }
  }, [searchParams, sessions]);

  const handleJoin = (session) => {
    setSelectedSession(session);
    setJoinOpen(true);
    setPaymentSuccess(false);
    setForm({ name: '', email: '', phone: '' });
  };

  const handleRazorpaySubscription = async () => {
    if (!selectedSession || !form.name || !form.email || !form.phone) return;
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API}/subscriptions/razorpay/create`, {
        session_id: selectedSession.id,
        origin_url: window.location.origin,
        name: form.name,
        email: form.email,
        phone: form.phone,
      });
      if (data.subscription_id && data.key_id) {
        const options = {
          key: data.key_id,
          subscription_id: data.subscription_id,
          name: 'Manifest with Dr. Dinesh',
          description: `${selectedSession.title} — Monthly Subscription`,
          handler: async function (response) {
            try {
              await axios.post(`${API}/subscriptions/razorpay/verify`, {
                razorpay_subscription_id: response.razorpay_subscription_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                session_id: selectedSession.id,
                name: form.name,
                email: form.email,
                phone: form.phone,
              });
              setSuccessSessionTitle(selectedSession.title);
              setPaymentSuccess(true);
            } catch {
              alert('Subscription verification failed. Please contact support.');
            }
          },
          prefill: { name: form.name, email: form.email, contact: form.phone },
          theme: { color: '#4338ca' },
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      }
    } catch (err) {
      console.error('Razorpay subscription error:', err);
      alert('Subscription failed. Please try again.');
    }
    setProcessing(false);
  };

  const handleStripeSubscription = async () => {
    if (!selectedSession || !form.name || !form.email || !form.phone) return;
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API}/subscriptions/stripe/create`, {
        session_id: selectedSession.id,
        origin_url: window.location.origin,
        name: form.name,
        email: form.email,
        phone: form.phone,
      });
      if (data.url) window.location.href = data.url;
    } catch {
      alert('Subscription failed. Please try again.');
    }
    setProcessing(false);
  };

  const handleLookup = async () => {
    if (!lookupEmail) return;
    setLookingUp(true);
    try {
      const { data } = await axios.post(`${API}/subscriptions/lookup`, { email: lookupEmail });
      setActiveSubs(data);
    } catch {
      setActiveSubs([]);
    }
    setLookingUp(false);
  };

  const handleCancel = async (sub) => {
    const subId = sub.razorpay_subscription_id || sub.stripe_subscription_id;
    if (!subId) {
      alert('This subscription cannot be cancelled yet. It may still be processing.');
      return;
    }
    if (!window.confirm(`Cancel your ${sub.session_title} subscription? It will remain active until the end of the current billing period.`)) return;
    setCancelling(subId);
    try {
      await axios.post(`${API}/subscriptions/cancel`, { subscription_id: subId, email: lookupEmail });
      handleLookup();
    } catch {
      alert('Cancellation failed. Please contact support.');
    }
    setCancelling(null);
  };

  const isFormValid = form.name && form.email && form.phone;

  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="py-24 sm:py-32 bg-gradient-to-br from-indigo-50 via-white to-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div variants={fadeUp} initial="hidden" animate="visible">
            <span className="text-xs sm:text-sm tracking-[0.2em] uppercase text-indigo-600 font-semibold">Daily Live Sessions</span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] text-slate-900 mt-4 mb-6">
              Live Yoga &{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-amber-600">Meditation Sessions</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto mb-6">
              Transform your mornings with daily live sessions led by Dr. Dinesh Mungath. Auto-renewing monthly subscriptions — cancel anytime.
            </p>
            <Button
              onClick={() => setManageOpen(true)}
              variant="outline"
              className="rounded-full gap-2 text-slate-600 border-slate-300 hover:bg-slate-100"
              data-testid="manage-subs-btn"
            >
              <RefreshCw className="w-4 h-4" /> Manage My Subscriptions
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Sessions Grid */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {sessions.map((session, i) => {
              const Icon = SESSION_ICONS[session.id] || Sun;
              const gradient = SESSION_GRADIENTS[session.id] || 'from-indigo-500 to-purple-500';
              const bgClass = SESSION_BG[session.id] || 'bg-slate-50 border-slate-100';
              const accent = SESSION_ACCENT[session.id] || 'text-indigo-600';

              return (
                <motion.div key={session.id} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}>
                  <div className={`rounded-2xl border overflow-hidden hover:shadow-xl transition-all duration-300 h-full flex flex-col ${bgClass}`} data-testid={`session-card-${session.id}`}>
                    {session.image && (
                      <div className="relative h-48 overflow-hidden">
                        <img src={session.image} alt={session.title} className="w-full h-full object-cover" data-testid={`session-image-${session.id}`} />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                        <div className={`absolute top-4 left-4 w-11 h-11 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <span className="absolute top-4 right-4 bg-white/90 backdrop-blur text-xs font-bold px-3 py-1.5 rounded-full text-indigo-700 flex items-center gap-1">
                          <RefreshCw className="w-3 h-3" /> Auto-renew
                        </span>
                      </div>
                    )}
                    <div className="p-8 sm:p-10 flex flex-col flex-1">
                      <h3 className="font-serif text-2xl font-semibold text-slate-900 mb-1">{session.title}</h3>
                      <div className={`flex items-center gap-1.5 mb-4 ${accent}`}>
                        <Clock className="w-4 h-4" />
                        <span className="text-sm font-semibold">Daily at {session.timing}</span>
                      </div>
                      <p className="text-sm text-slate-600 leading-relaxed mb-6">{session.description}</p>
                      <ul className="space-y-3 mb-8 flex-1">
                        {session.features?.map((f, fi) => (
                          <li key={fi} className="flex items-start gap-2 text-sm text-slate-700">
                            <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />{f}
                          </li>
                        ))}
                      </ul>
                      <div className="pt-6 border-t border-slate-200/60">
                        <div className="flex items-end justify-between mb-4">
                          <div>
                            <p className="text-3xl font-bold text-slate-900 font-serif">&#8377;{session.price_inr}<span className="text-sm font-normal text-slate-500">/month</span></p>
                            <p className="text-xs text-slate-500 mt-0.5">${session.price} USD/month for International</p>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleJoin(session)}
                          className={`w-full rounded-full text-white bg-gradient-to-r ${gradient} hover:opacity-90 gap-2 py-6 text-base font-semibold shadow-lg transition-all hover:scale-[1.02]`}
                          data-testid={`join-btn-${session.id}`}
                        >
                          Subscribe Now <ArrowRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Subscribe Dialog */}
      <Dialog open={joinOpen} onOpenChange={setJoinOpen}>
        <DialogContent className="sm:max-w-[480px] p-0 overflow-hidden" data-testid="join-dialog">
          {paymentSuccess ? (
            <div className="p-8 sm:p-10 text-center">
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-emerald-600" />
              </div>
              <h3 className="font-serif text-2xl font-semibold text-slate-900 mb-2" data-testid="payment-success-title">Subscription Activated!</h3>
              <p className="text-slate-600 text-sm mb-2">
                You are now subscribed to <strong>{successSessionTitle}</strong>.
              </p>
              <p className="text-slate-500 text-xs mb-6">Your subscription auto-renews monthly. You can cancel anytime from "Manage My Subscriptions".</p>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi Dr. Dinesh, I have subscribed to ${successSessionTitle}. Please share the Zoom link.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-redirect-link"
              >
                <Button className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 text-white py-6 text-base font-bold gap-2 shadow-lg" data-testid="whatsapp-redirect-btn">
                  <MessageCircle className="w-5 h-5" /> Message Dr. Dinesh on WhatsApp
                </Button>
              </a>
              <p className="text-slate-400 text-xs mt-3">Send a WhatsApp message to receive your daily Zoom link</p>
            </div>
          ) : (
            <>
              <div className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-slate-800 p-6 sm:p-8 text-white">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl text-white mb-1">{selectedSession?.title}</DialogTitle>
                  <DialogDescription className="text-white/70 text-sm">
                    Daily at {selectedSession?.timing} | Monthly auto-renewing subscription
                  </DialogDescription>
                </DialogHeader>
                <div className="flex items-end gap-3 mt-4">
                  <span className="text-amber-400 font-bold text-4xl font-serif">&#8377;{selectedSession?.price_inr}</span>
                  <span className="text-white/50 text-base mb-1">/month</span>
                </div>
                <p className="text-white/50 text-xs mt-1">Cancel anytime from "Manage My Subscriptions"</p>
              </div>
              <div className="p-6 sm:p-8 space-y-4" data-testid="join-form">
                <Input placeholder="Your Full Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="h-12 rounded-xl" required data-testid="yoga-name-input" />
                <Input type="email" placeholder="Your Email Address" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="h-12 rounded-xl" required data-testid="yoga-email-input" />
                <Input placeholder="Your Phone Number" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="h-12 rounded-xl" required data-testid="yoga-phone-input" />
                <Button
                  type="button"
                  onClick={handleRazorpaySubscription}
                  disabled={processing || !isFormValid}
                  className="w-full rounded-full bg-amber-500 hover:bg-amber-600 text-white h-14 text-lg font-bold shadow-lg shadow-amber-500/30"
                  data-testid="yoga-razorpay-btn"
                >
                  {processing ? 'Processing...' : `Subscribe \u20B9${selectedSession?.price_inr}/month (India - Razorpay)`}
                </Button>
                <Button
                  type="button"
                  onClick={handleStripeSubscription}
                  disabled={processing || !isFormValid}
                  className="w-full rounded-full bg-indigo-500 hover:bg-indigo-600 text-white h-12 text-base font-semibold"
                  data-testid="yoga-stripe-btn"
                >
                  {processing ? 'Processing...' : `Subscribe $${selectedSession?.price}/month (International - Stripe)`}
                </Button>
                <p className="text-slate-400 text-xs text-center">Auto-renewing monthly. Cancel anytime.</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Manage Subscriptions Dialog */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="sm:max-w-[520px]" data-testid="manage-subs-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Manage My Subscriptions</DialogTitle>
            <DialogDescription>Enter your email to view and manage your active subscriptions.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Enter your email"
                value={lookupEmail}
                onChange={e => setLookupEmail(e.target.value)}
                className="h-12 rounded-xl flex-1"
                data-testid="lookup-email-input"
              />
              <Button onClick={handleLookup} disabled={lookingUp || !lookupEmail} className="h-12 rounded-xl px-6 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="lookup-btn">
                {lookingUp ? '...' : 'Look Up'}
              </Button>
            </div>

            {activeSubs.length > 0 ? (
              <div className="space-y-3" data-testid="active-subs-list">
                {activeSubs.map((sub, i) => (
                  <div key={i} className="bg-slate-50 rounded-xl p-4 border border-slate-200 flex items-center justify-between" data-testid={`sub-item-${i}`}>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">{sub.session_title}</p>
                      <p className="text-xs text-slate-500">{sub.provider === 'razorpay' ? `Rs.${sub.amount_inr}/month` : `$${sub.amount_usd}/month`} via {sub.provider}</p>
                      <span className={`text-xs font-bold mt-1 inline-block px-2 py-0.5 rounded-full ${sub.status === 'active' ? 'bg-emerald-100 text-emerald-700' : sub.status === 'cancelling' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                        {sub.status === 'active' ? 'Active' : sub.status === 'cancelling' ? 'Cancelling at period end' : sub.status}
                      </span>
                    </div>
                    {sub.status === 'active' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCancel(sub)}
                        disabled={cancelling === (sub.razorpay_subscription_id || sub.stripe_subscription_id)}
                        className="text-red-600 border-red-200 hover:bg-red-50 gap-1 text-xs"
                        data-testid={`cancel-sub-btn-${i}`}
                      >
                        <XCircle className="w-3.5 h-3.5" /> Cancel
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : lookupEmail && !lookingUp ? (
              <div className="text-center py-8 text-slate-400" data-testid="no-subs-found">
                <Mail className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">No active subscriptions found for this email.</p>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
